import { ChevronDownIcon } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "../../i18n";
import { cn } from "../../utils";
import { Select } from "../Select";
import type { SelectOption } from "../Select/types";
import { Skeleton } from "../Skeleton";
import { usePaginationContext } from "./context";

export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export const PageSizeControls = () => {
  const { t } = useTranslation();
  const { page, pageSize, totalItems, onPageSizeChange, loading, disabled } =
    usePaginationContext();

  const options: SelectOption[] = useMemo(() => {
    return DEFAULT_PAGE_SIZE_OPTIONS.map((size) => ({
      id: size.toString(),
      text: size.toString(),
    }));
  }, []);

  const value = pageSize.toString();

  const handleChange = useCallback(
    (nextPageSize: string | undefined) => {
      if (onPageSizeChange) {
        onPageSizeChange(Number(nextPageSize));
      }
    },
    [onPageSizeChange],
  );

  const itemsFrom = (page - 1) * pageSize + 1;
  const itemsTo = Math.min(page * pageSize, totalItems);

  const onRenderAnchor = useCallback(
    (open?: boolean) => {
      return (
        <button
          type="button"
          className={cn(
            "flex items-center justify-between gap-1 h-6 px-1.5 rounded-md",
            "bg-neutral-500/5 hover:bg-primary-500/10 focus-visible:bg-primary-500/10 active:bg-primary-500/15",
            "focus-visible:outline focus-visible:outline-1 focus-visible:outline-primary-500 focus-visible:outline-offset-0",
          )}
          disabled={disabled}
          data-e2e-id="page-size-controls"
          style={{ outlineOffset: "1px" }}
        >
          <span className="text-xs text-neutral-500">{value}</span>
          <ChevronDownIcon
            className={cn(
              "transition-transform duration-300",
              open && "rotate-180",
            )}
            size={14}
          />
        </button>
      );
    },
    [value, disabled],
  );

  if (loading) {
    return <Skeleton className="h-6 w-[200px]" />;
  }

  if (!onPageSizeChange) {
    return null;
  }

  return (
    <div className="flex items-center">
      <span className="text-xs text-neutral-500 me-1">
        {t("pagination.show")}
      </span>

      <Select
        options={options}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        size="sm"
        onRenderAnchor={onRenderAnchor}
        ariaLabel={t("pagination.itemsPerPage")}
        matchTriggerWidth={false}
        minWidth={100}
      />

      <span className="hidden sm:inline text-xs text-neutral-500 ms-4">
        {t("pagination.itemsRange", {
          from: itemsFrom,
          to: itemsTo,
          total: totalItems,
        })}
      </span>
    </div>
  );
};
